import React from 'react';
import styled from 'styled-components';
import { updateMetaTags, resetMetaTags } from '../../utils/metaTags';

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const ModalWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalContent = styled.div`
  background: #151515;
  border-radius: 16px;
  padding: 24px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const CertificateImage = styled.img`
  max-width: 100%;
  max-height: 60vh;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const ShareSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const ShareTitle = styled.h3`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  text-align: center;
`;

const ShareButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const SocialButton = styled.button<{ disabled?: boolean }>`
  background: none;
  border: none;
  border-radius: 12px;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  transition: all 0.2s ease;
  padding: 0;
  color: #ffffff;

  &:hover:not(:disabled) {
    opacity: 0.8;
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -35px;
  right: -40px;
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  min-width: 40px;
  max-width: 40px;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  color: white;
  font-size: 50px;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 1001;

  @media (max-width: 768px) {
    top: -35px;
    right: -10px;
  }

  &:hover {
    opacity: 0.8;
    transform: scale(1.1);
  }
`;

interface CertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  certificateImage: string;
  shareTitleText?: string;
  shareText?: string;
  shareTextTwitter?: string;
  shareErrorText?: string;
  certificateAltText?: string;
}

export const CertificateModal: React.FC<CertificateModalProps> = ({
  isOpen,
  onClose,
  certificateImage,
  shareTitleText = 'Поделиться',
  shareText = 'Я завершил курс DeFi на STONfi! Получил сертификат 🎓',
  shareTextTwitter = '🎓 Я завершил курс DeFi на @ston_fi и получил сертификат!',
  shareErrorText = 'Ошибка при попытке поделиться сертификатом',
  certificateAltText = 'Сертификат STONfi.pools',
}) => {
  const [isSharing, setIsSharing] = React.useState<string | null>(null);

  // Определяем, является ли устройство мобильным
  const isMobileDevice = () => {
    const userAgent =
      navigator.userAgent ||
      navigator.vendor ||
      (window as { opera?: string }).opera ||
      '';
    // Проверяем на mobile устройства
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent.toLowerCase()
    );
  };

  // Обновляем мета-теги при открытии модалки
  React.useEffect(() => {
    if (isOpen && certificateImage && !certificateImage.startsWith('blob:')) {
      updateMetaTags(certificateImage, 'STONfi DeFi Certificate', shareText);
    }

    return () => {
      // Восстанавливаем стандартные мета-теги при закрытии
      if (!isOpen) {
        resetMetaTags();
      }
    };
  }, [isOpen, certificateImage, shareText]);

  const handleShare = async (platform: 'telegram' | 'x' | 'linkedin') => {
    try {
      setIsSharing(platform);

      // Проверяем, что это URL из Supabase, а не локальный blob URL
      const isBlobUrl = certificateImage.startsWith('blob:');
      if (isBlobUrl) {
        alert(
          shareErrorText +
            '\n\nСертификат не был загружен в Supabase. Пожалуйста, попробуйте снова.'
        );
        setIsSharing(null);
        return;
      }

      // ===== ПОПЫТКА 1: Web Share API (прямая отправка изображения) =====
      // Используем Web Share API ТОЛЬКО на мобильных устройствах
      // На desktop это часто не работает или вызывает ошибки
      const isMobile = isMobileDevice();
      console.log('Device type:', isMobile ? 'Mobile' : 'Desktop');

      if (isMobile) {
        try {
          // Проверяем поддержку Web Share API
          if (navigator.share && navigator.canShare) {
            console.log(
              'Web Share API supported, attempting to share image...'
            );

            // Загружаем изображение
            const response = await fetch(certificateImage);
            const blob = await response.blob();
            const file = new File([blob], 'stonfi-certificate.png', {
              type: 'image/png',
            });

            // Проверяем, можем ли мы шарить файлы
            const canShareFiles = navigator.canShare({ files: [file] });
            console.log('Can share files:', canShareFiles);

            if (canShareFiles) {
              // Пытаемся поделиться через Web Share API
              await navigator.share({
                files: [file],
                title: 'STONfi DeFi Certificate',
                text: shareText,
              });

              console.log('Successfully shared via Web Share API');
              setIsSharing(null);
              return; // Успех! Выходим из функции
            }
          }
        } catch (webShareError) {
          // Web Share API не сработал или пользователь отменил
          // Это нормально, продолжаем с URL-based подходом
          console.log(
            'Web Share API failed, falling back to URL sharing:',
            webShareError
          );
        }
      } else {
        console.log(
          'Desktop detected, skipping Web Share API, using URL-based sharing'
        );
      }

      // ===== FALLBACK: URL-based шеринг (текущий подход) =====
      let shareUrl = '';

      const apiBaseUrl =
        import.meta.env.STON_ACADEMY_API_URL || window.location.origin;
      const imageIdMatch = certificateImage.match(/certificate-(\d+)\.png/);
      const imageId = imageIdMatch ? imageIdMatch[1] : '';
      const certificatePageUrl = imageId
        ? `${apiBaseUrl}/certificate?id=${imageId}`
        : `${apiBaseUrl}/certificate?id=unknown`;

      switch (platform) {
        case 'telegram': {
          // Для Telegram используем стандартное кодирование через encodeURIComponent
          // Проблема с отображением эмодзи может быть связана с версией Telegram или браузера
          // Используем window.open вместо Telegram.WebApp.openLink для лучшей совместимости
          const telegramTextEncoded = encodeURIComponent(shareText);
          shareUrl = `https://t.me/share/url?url=${certificatePageUrl}&text=${telegramTextEncoded}`;
          break;
        }
        case 'x': {
          // Для X/Twitter используем URL страницы сертификата с мета-тегами
          // Twitter Card требует мета-теги на странице для отображения превью
          // Используем серверный endpoint /certificate который отдает HTML с правильными мета-тегами
          // Извлекаем ID из URL изображения для короткой ссылки
          const xText = `${shareTextTwitter}\n\n${certificatePageUrl}`;
          shareUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(xText)}`;
          break;
        }
        case 'linkedin': {
          // Для LinkedIn используем только URL
          // LinkedIn автоматически получит Open Graph мета-теги со страницы
          // Параметр summary больше не поддерживается
          shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certificatePageUrl)}`;
          break;
        }
      }

      // Проверяем, находимся ли мы в Telegram WebApp
      const openLinkFn = (
        window.Telegram?.WebApp as unknown as {
          openLink?: (url: string) => void;
        }
      )?.openLink;

      if (typeof openLinkFn === 'function' && platform === 'telegram') {
        // Для Telegram WebApp используем window.open вместо openLink
        // чтобы избежать проблем с двойным кодированием эмодзи
        // openLink может некорректно обрабатывать уже закодированные URL
        window.open(shareUrl, '_blank', 'noopener');
      } else {
        window.open(shareUrl, '_blank', 'noopener');
      }
    } catch (error) {
      console.error('Ошибка шеринга:', error);
      alert(shareErrorText);
    } finally {
      setIsSharing(null);
    }
  };

  // SVG иконки для соцсетей
  const TelegramIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.13-.31-1.09-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"
        fill="currentColor"
      />
    </svg>
  );

  const XIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
        fill="currentColor"
      />
    </svg>
  );

  const LinkedInIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
        fill="currentColor"
      />
    </svg>
  );

  const DownloadIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 16L12 8M12 16L8 12M12 16L16 12M6 20H18C18.5523 20 19 19.5523 19 19V18M5 18V19C5 19.5523 5.44772 20 6 20"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 18H19"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );

  const handleDownload = async () => {
    try {
      setIsSharing('download');

      console.log('certificateImage', certificateImage);

      if (
        typeof window !== 'undefined' &&
        window.Telegram?.WebApp &&
        window.Telegram.WebApp.initDataUnsafe?.user
      ) {
        try {
          window.Telegram.WebApp.downloadFile({
            url: certificateImage,
            file_name: 'stonfi-certificate.png',
          });
        } catch (error) {
          console.error('Download error:', error);
        }
      } else {
        // Fallback to browser-based download
        console.log('Using browser-based download');

        // Fetch the image and create a blob
        const response = await fetch(certificateImage);
        const blob = await response.blob();

        // Create a download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'stonfi-certificate.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download error:', error);
      alert(shareErrorText);
    } finally {
      setIsSharing(null);
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={onClose}>
      <ModalWrapper onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        <ModalContent>
          <CertificateImage
            src={certificateImage}
            alt={certificateAltText}
            onError={() => {
              console.error('Failed to load certificate image');
            }}
            onLoad={() => {
              console.log('Certificate image loaded successfully');
            }}
          />

          <ShareSection>
            <ShareTitle>{shareTitleText}</ShareTitle>
            <ShareButtonsContainer>
              <SocialButton
                onClick={() => handleShare('telegram')}
                disabled={isSharing !== null}
                title="Telegram"
              >
                <TelegramIcon />
              </SocialButton>
              <SocialButton
                onClick={() => handleShare('x')}
                disabled={isSharing !== null}
                title="X (Twitter)"
              >
                <XIcon />
              </SocialButton>
              <SocialButton
                onClick={() => handleShare('linkedin')}
                disabled={isSharing !== null}
                title="LinkedIn"
              >
                <LinkedInIcon />
              </SocialButton>
              <SocialButton
                onClick={handleDownload}
                disabled={isSharing !== null}
                title="Download"
              >
                <DownloadIcon />
              </SocialButton>
            </ShareButtonsContainer>
          </ShareSection>
        </ModalContent>
      </ModalWrapper>
    </ModalOverlay>
  );
};
